// src/lib/layer_style.ts — STILUL EFECTIV al unui strat (PUR, partajat server↔client, ca
// lib/layer_order). Sursa UNICĂ a paletei și a valorilor implicite de desenare.
//
// De ce există: culoarea pe care o vede utilizatorul NU e stocată pe server pentru straturile fără
// stil explicit — se alege din paletă după `color_index`, iar paleta trăia doar în client. Un export
// construit din `config.style` ar fi livrat simbologie greșită pentru fiecare strat de pe paletă și ar
// fi părut că funcționează. Pe datele reale de pe producție (34 de GeoPackage-uri de proiectant, 608
// stiluri), 87 de stiluri (14,3 %) au QML dar SLD gol, iar importul citește culoarea DOAR din SLD:
// unul din șapte straturi stilizate de proiectant ajunge la noi fără culoare și cade pe paletă.
//
// La fel și valorile implicite (opacitate 0,7, contur = culoarea, grosimi, raza cercului): dacă ar
// rămâne în client, QML-ul exportat ar avea alt contur decât harta. Harta, legenda, planșa tipărită și
// exportul citesc TOATE de aici — REQ-LIV-041 („aceleași simboluri ca planșele tipărite") se
// satisface prin construcție, nu prin text.

export const LAYER_COLORS = [
	"#0891b2",
	"#16a34a",
	"#d97706",
	"#db2777",
	"#7c3aed",
	"#0284c7",
	"#65a30d",
	"#ca8a04",
	"#ea580c",
	"#475569",
]

export const DEFAULT_OPACITY = 0.7
export const POLYGON_OUTLINE_PX = 1
export const LINE_WIDTH_PX = 2
export const POINT_RADIUS_PX = 5
export const POINT_STROKE_PX = 1
export const LABEL_SIZE_PX = 12
export const LABEL_HALO_PX = 1.5
export const LABEL_COLOR = "#1a1a1a"
export const LABEL_HALO_COLOR = "#ffffff"

export type StyleFamily = "polygon" | "line" | "point"

/** Ce știe un strat despre stilul lui — forma comună a `LayerRef` (server) și `LayerSummary` (client). */
export interface LayerStyleSource {
	style?: object | null
	color_index?: number
	geometry_type?: string
	icon_url?: string
}

export interface EffectiveStyle {
	family: StyleFamily
	color: string // culoarea principală: fill (poligon), linie, cerc (punct)
	opacity: number // 0..1
	stroke: string // conturul poligonului / cercului
	width: number // grosimea conturului (poligon), a liniei (linie), a conturului cercului (punct), în px
	label: string // atributul de etichetare sau ""
	has_icon: boolean // punct desenat cu iconiță (în loc de cerc)
	palette: string // culoarea de PALETĂ a stratului — rezerva exportului pentru culori CSS care nu se traduc în hex
	// 09.10 (Popești-Leordeni): ce mai știa stilul proiectantului și pierdeam la import.
	dash: boolean // linie/contur întrerupt
	label_size: number // mărimea etichetei, px (implicit LABEL_SIZE_PX)
	label_color: string // culoarea etichetei (implicit LABEL_COLOR)
	categories: EffectiveCategories | null // stil PE VALOARE (redarea pe reguli/categorii a proiectantului)
	// 09.10 (seara, după capturile de pe staging): ordinea de desenare și scara etichetelor.
	sort: "area" | "" // "area" = poligoanele MARI se desenează primele (dedesubt) — `$area` desc din QGIS
	label_scale_max: number // eticheta apare doar sub scara 1:N (numitorul QGIS `scaleMax`); 0 = mereu
	hatch: Hatch | null // hașura stilului de bază (B, 13.10)
}

/**
 * ORDINEA DE DESENARE (09.10): zonificarea convertită din DWG are poligoane uriașe cu cod (hașura T1 pe
 * tot UAT-ul, limita intravilanului codată „ID") PESTE zonele mici; proiectantul le ține dedesubt cu
 * `enableorderby` + `$area` descrescător. Fără regula asta ordinea dintr-un strat e cea a tile-ului — și
 * se schimbă de la un zoom la altul (văzut pe staging: violetul T1 acoperea orașul la unele zoom-uri).
 * Cheia de sortare e `AREA_KEY` pe proprietăți: tile-ul o calculează în SQL, sursa GeoJSON în browser.
 */
export const AREA_KEY = "__area"

/** Proprietatea de sortare MapLibre (`fill-sort-key`/`line-sort-key`): aria cu minus — mare = dedesubt. */
export function area_sort_key(): unknown[] {
	return ["*", -1, ["coalesce", ["to-number", ["get", AREA_KEY]], 0]]
}

/**
 * Zoom-ul MapLibre de la care se vede o etichetă cu `scaleMax` 1:N (QGIS, 96 dpi) la latitudinea dată:
 * la zoom z o hartă cu tile-uri de 512 px are 78271,517·cos(lat)/2^z m/px, iar numitorul scării e
 * m/px × 3779,5 (px per metru la 96 dpi). 1:10.000 la 44,4° N ≈ zoom 14,4; 1:40.000 ≈ 12,4.
 */
export function label_min_zoom(scale_max: number, lat: number): number {
	if (!(scale_max > 0)) {
		return 0
	}
	const m_per_px = scale_max / 3779.5275
	const z = Math.log2((78271.517 * Math.cos((lat * Math.PI) / 180)) / m_per_px)
	return Number.isFinite(z) ? Math.max(0, Math.min(24, Math.round(z * 100) / 100)) : 0
}

/**
 * STILUL PE VALOARE (09.10.2026, PUG Popești-Leordeni — REQ-BD-007 „simbolurile planșei"): proiectanții
 * livrează zonificarea colorată pe VALOARE (QGIS `RuleRenderer` cu „cad_layer" = '2_HASS_L1' → galben,
 * sau `categorizedSymbol` pe `zona`), iar un singur simbol per strat o aplatiza într-o culoare. Modelul
 * nostru păstrează DOAR ce se poate desena, citi și scrie identic pe tot lanțul (hartă, tile-uri,
 * legendă, tipărire, QML/SLD): reguli de EGALITATE sau de PREFIX pe UN atribut, cu un stil per valoare
 * și o regulă „altfel" (`else`). Valoarea se compară ca TEXT (`to-string` în expresie, `'…'` în QGIS).
 *
 * Fiecare categorie moștenește ce nu declară: culoarea lipsă = a stratului; conturul lipsă = conturul
 * EXPLICIT al stratului dacă există, altfel culoarea categoriei (regula de bază, unde conturul cade pe
 * culoare); grosimea și opacitatea lipsă = ale stratului. `else` absent = stilul de bază al stratului —
 * un obiect fără nicio valoare potrivită se desenează ca înainte, nu dispare.
 *
 * ⚠️ `opacity: 0` înseamnă FĂRĂ umplere (poligon: doar contur), FĂRĂ simbol (punct: doar eticheta) —
 * exact `style="no"` / `nullSymbol` din QGIS, care sunt starea NORMALĂ a planșelor CAD convertite: din
 * 72 de stiluri de poligon ale Popeștiului, 70 sunt doar contur.
 */
export const MAX_CATEGORIES = 64
export const DASH_ARRAY: readonly [number, number] = [3, 2]

export type CategoryMatch = "eq" | "prefix"

/**
 * CONDIȚIILE unei categorii (15.10 — regulile COMPUSE ale proiectanților: zonificările de la Armeniș,
 * Măxineni, Crețeni, Oravița, Cărpiniș, Roman): o categorie = conjuncție (ȘI) de condiții, fiecare pe UN
 * atribut, cu operatorul ei. Operandul poate fi NORMALIZAT ca în fișierele reale — `upper(trim(coalesce("a",
 * '')))` — prin `fold` (comparare fără majuscule/minuscule; literalul se păstrează deja pliat) și `trim`
 * (spațiile de la margini) și poate avea un atribut de REZERVĂ (`CASE WHEN "a" IS NOT NULL AND trim("a") <>
 * '' THEN "a" ELSE "b" END` — zonificarea Cărpinișului: subzona dacă există, altfel zona). ⚠️ `trim` n-are
 * echivalent în expresiile MapLibre: pe hartă se ignoră (o valoare cu spații la margini cade pe „altfel”),
 * în QGIS se scrie înapoi. Forma SIMPLĂ istorică (`{value, match}` pe `categories.attr`) rămâne forma
 * stocată a condiției unice de egalitate/prefix fără normalizare — stilurile deja scrise nu se schimbă.
 */
export type CategoryOp =
	| "eq"
	| "neq"
	| "in"
	| "not_in"
	| "prefix"
	| "suffix"
	| "contains"
	| "empty"
	| "not_empty"
export const CATEGORY_OPS: readonly CategoryOp[] = [
	"eq",
	"neq",
	"in",
	"not_in",
	"prefix",
	"suffix",
	"contains",
	"empty",
	"not_empty",
]
export interface CategoryCondition {
	attr: string
	op: CategoryOp
	values: string[] // literalele (pliate după `fold`); goale la empty/not_empty
	fold: "" | "upper" | "lower"
	trim: boolean
	fallback: string // atributul de rezervă ("" = fără)
}

export interface StyleCategoryInput {
	value?: string
	match?: CategoryMatch // implicit "eq"
	conditions?: unknown[] // forma COMPUSĂ: [{attr, op, values, fold?, trim?, fallback?}]
	label?: string
	color?: string
	opacity?: number
	stroke?: string
	width?: number
}

export interface CategorySymbol {
	color: string
	opacity: number
	stroke: string
	width: number
	hatch: Hatch | null // hașura (B, 13.10) — a simbolului, NU se moștenește din stilul de bază
}

export interface EffectiveCategory extends CategorySymbol {
	value: string // literalul (forma simplă) sau textul condițiilor (forma compusă) — pentru afișare/dedupe
	match: CategoryMatch
	label: string
	conditions: CategoryCondition[] // MEREU populat: forma simplă = o condiție eq/prefix pe `attr`
}

export interface EffectiveCategories {
	attr: string // atributul PRINCIPAL (cel mai des folosit) — forma simplă a valorilor se referă la el
	attrs: string[] // TOATE atributele folosite de condiții (+ rezervele), principalul primul: tile-uri, F10, export
	values: EffectiveCategory[]
	else: CategorySymbol // REZOLVAT: regula „altfel" explicită sau stilul de bază
	has_else: boolean // regula „altfel" a fost declarată explicit (se scrie ca atare în QGIS)
}

/**
 * Simbol ASCUNS (12.10): categoria debifată de proiectant în QGIS sau „altfel" fără regulă — nici
 * umplere, nici contur, deci obiectele nu se desenează. Panoul le arată estompat, legenda și
 * tipărirea le omit (explică doar ce se vede).
 */
export const is_hidden_symbol = (s: CategorySymbol): boolean => s.opacity === 0 && s.width === 0

/**
 * HAȘURA (B, 13.10.2026): singura decorație a planșelor pe care harta o desenează CA ATARE — linii paralele
 * (sau încrucișate) peste o umplere de regulă transparentă (zonele de protecție, riscul la inundații,
 * siturile Natura 2000: 87 de straturi, 190 de simboluri, 31 încrucișate în cele 19 fișiere de proiectant).
 * Modelul e MĂRGINIT deliberat la patru câmpuri; restul decorului proiectantului (markeri pe contur, a doua
 * linie, modele de puncte, SVG) rămâne pe nuanța plată a lui `read_patterns`, iar originalul lui se va
 * păstra la export într-o lucrare separată. Harta cuantizează unghiul la 0/45/90/135 și latura imaginii la o
 * putere a lui 2 (MapLibre cere imagini de model pătrate, de 2^n px, ca să se repete fără cusur); QGIS
 * primește înapoi valorile exacte. Ce nu se traduce în model cade pe nuanța plată, cu pragul ei de 20 %.
 */
export interface Hatch {
	color: string // culoarea liniilor (CSS; hex la export)
	angle: number // grade, 0–179 (QGIS: 45 implicit)
	spacing_px: number // distanța dintre linii, px (2–64)
	crossed: boolean // două direcții: unghiul și unghiul + 90°
}
export const HATCH_SPACING_PX = 8
export const HATCH_ANGLE = 45

/** Citire TOLERANTĂ a lui `hatch` (JSON liber): culoarea lipsă = a simbolului; unghi/distanță cu implicite și plafoane. */
export function parse_hatch(raw: unknown, fallback_color: string): Hatch | null {
	if (typeof raw !== "object" || raw === null) {
		return null
	}
	const h = raw as Record<string, unknown>
	const angle = ((Math.round(num_or(h.angle, HATCH_ANGLE, -36_000, 36_000)) % 180) + 180) % 180
	return {
		color: typeof h.color === "string" && h.color !== "" ? h.color : fallback_color,
		angle,
		spacing_px: Math.round(num_or(h.spacing_px, HATCH_SPACING_PX, 2, 64) * 100) / 100,
		crossed: h.crossed === true,
	}
}

/** Unghiul DESENAT pe hartă: 0 / 45 / 90 / 135 — singurele direcții care se repetă fără cusur într-o imagine pătrată. */
export function hatch_angle_q(angle: number): 0 | 45 | 90 | 135 {
	const a = ((Math.round(angle) % 180) + 180) % 180
	const q = Math.round(a / 45) * 45
	return (q >= 180 ? 0 : q) as 0 | 45 | 90 | 135
}

/**
 * Latura imaginii de model (px CSS): puterea lui 2 cea mai apropiată de distanța dintre linii — pe
 * diagonală distanța se măsoară perpendicular pe linii, deci latura e distanța × √2 —, între 8 și 64.
 */
export function hatch_tile_px(h: Hatch): number {
	const q = hatch_angle_q(h.angle)
	const d = h.spacing_px * (q === 45 || q === 135 ? Math.SQRT2 : 1)
	const p = Math.round(Math.log2(Math.max(8, Math.min(64, d))))
	return 2 ** Math.max(3, Math.min(6, p))
}

/** Numele imaginii de model: O singură imagine per combinație distinctă, refolosită între straturi și hărți. */
export function hatch_image_name(h: Hatch): string {
	return `hatch:${to_hex(h.color) || "#64748b"}:${hatch_angle_q(h.angle)}:${hatch_tile_px(h)}:${h.crossed ? 1 : 0}`
}

/**
 * Stratul MapLibre de hașură al unui strat tematic (`<id>__hatch`, un `fill` cu `fill-pattern` peste
 * umplerea obișnuită): `pattern` = numele imaginii (constant fără categorii; `case` pe atributul
 * categoriilor cu ele — DOAR categoriile hașurate, nehașuratele cad pe `filter`), `filter` = obiectele care
 * primesc hașură (null = toate), `images` = imaginile de generat. null = stratul n-are nicio hașură.
 * Categoriile ASCUNSE nu primesc hașură chiar dacă ar purta una.
 */
export function hatch_layer_spec(es: EffectiveStyle): {
	pattern: PaintValue
	filter: unknown[] | null
	images: { name: string; hatch: Hatch }[]
} | null {
	const c = es.categories
	if (!c) {
		if (!es.hatch) {
			return null
		}
		const name = hatch_image_name(es.hatch)
		return { pattern: name, filter: null, images: [{ name, hatch: es.hatch }] }
	}
	const cond = category_expr
	const hatched = c.values.filter((v) => v.hatch && !is_hidden_symbol(v))
	const plain = c.values.filter((v) => !v.hatch || is_hidden_symbol(v))
	const else_hatch = is_hidden_symbol(c.else) ? null : c.else.hatch
	if (hatched.length === 0 && !else_hatch) {
		return null
	}
	const images = new Map<string, Hatch>()
	const expr: unknown[] = ["case"]
	for (const v of hatched) {
		const name = hatch_image_name(v.hatch!)
		images.set(name, v.hatch!)
		expr.push(cond(v), ["image", name])
	}
	const fallback = else_hatch ?? hatched[0].hatch!
	const fallback_name = hatch_image_name(fallback)
	images.set(fallback_name, fallback)
	expr.push(["image", fallback_name])
	const filter: unknown[] | null = else_hatch
		? plain.length === 0
			? null
			: ["!", ["any", ...plain.map(cond)]]
		: ["any", ...hatched.map(cond)]
	return {
		pattern: expr,
		filter,
		images: [...images.entries()].map(([name, hatch]) => ({ name, hatch })),
	}
}

const as_style = (s: unknown): Record<string, unknown> =>
	typeof s === "object" && s !== null ? (s as Record<string, unknown>) : {}

/**
 * Familia de desenare — EXACT ramificarea din client: poligon dacă tipul conține „polygon", linie
 * dacă „line"/„string"/„curve", altfel punct (inclusiv un strat netipat, care se desenează ca puncte).
 * ⚠️ Nu e `geometry_family` din lib/geojson (aceea răspunde la altă întrebare și dă „" pe netipat).
 */
export function style_family(geometry_type: string | undefined): StyleFamily {
	const gt = (geometry_type ?? "").toLowerCase()
	if (gt.includes("polygon")) {
		return "polygon"
	}
	if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		return "line"
	}
	return "point"
}

/**
 * Culoarea unui strat: cea din stil dacă există, altfel din paletă după `color_index` (poziția
 * ISTORICĂ, calculată pe server) — nu după poziția din lista afișată, pe care primăria o poate
 * reordona. `fallback_idx` acoperă doar răspunsurile vechi din cache-ul PWA, unde câmpul lipsește.
 * ⚠️ Semnătura primește STRATUL primul: orice sit de apel rămas pe forma veche (indice, strat) e o
 * eroare de compilare, nu o culoare greșită descoperită la primărie.
 */
export function layer_color(layer: LayerStyleSource, fallback_idx: number): string {
	const c = as_style(layer.style).color
	return typeof c === "string" && c !== "" ? c : palette_color(layer, fallback_idx)
}

/** Culoarea de PALETĂ a stratului (după `color_index`, altfel după `fallback_idx`), indiferent de `style.color`. */
export function palette_color(layer: LayerStyleSource, fallback_idx: number): string {
	const ci = layer.color_index
	const idx = typeof ci === "number" && Number.isInteger(ci) && ci >= 0 ? ci : fallback_idx
	return LAYER_COLORS[((idx % LAYER_COLORS.length) + LAYER_COLORS.length) % LAYER_COLORS.length]
}

/** Stilul efectiv al unui strat, cu implicitele de desenare aplicate. */
export function effective_style(layer: LayerStyleSource, fallback_idx: number): EffectiveStyle {
	const s = as_style(layer.style)
	const family = style_family(layer.geometry_type)
	const color = layer_color(layer, fallback_idx)
	const opacity =
		typeof s.opacity === "number" && Number.isFinite(s.opacity)
			? Math.min(1, Math.max(0, s.opacity))
			: DEFAULT_OPACITY
	const stroke = typeof s.stroke === "string" && s.stroke !== "" ? s.stroke : color
	const w =
		typeof s.width === "number" && Number.isFinite(s.width) && s.width >= 0 ? s.width : undefined
	// 09.10: conturul PUNCTULUI urmează și el stilul (implicit POINT_STROKE_PX) — până atunci era fixat
	// la 1 px pe hartă, iar exportul nu putea scrie înapoi grosimea citită din fișierul proiectantului.
	const width =
		w ??
		(family === "point" ? POINT_STROKE_PX : family === "line" ? LINE_WIDTH_PX : POLYGON_OUTLINE_PX)
	const label = typeof s.label === "string" ? s.label : ""
	const base: CategorySymbol = { color, opacity, stroke, width, hatch: parse_hatch(s.hatch, color) }
	const explicit_stroke = typeof s.stroke === "string" && s.stroke !== ""
	return {
		family,
		color,
		opacity,
		stroke,
		width,
		label,
		has_icon: family === "point" && typeof layer.icon_url === "string" && layer.icon_url !== "",
		palette: palette_color(layer, fallback_idx),
		dash: s.dash === true,
		hatch: base.hatch,
		sort: s.sort === "area" ? "area" : "",
		label_scale_max: num_or(s.label_scale_max, 0, 0, 100_000_000),
		label_size: num_or(s.label_size, LABEL_SIZE_PX, 4, 64),
		label_color:
			typeof s.label_color === "string" && s.label_color !== "" ? s.label_color : LABEL_COLOR,
		categories: parse_categories(s.categories, base, explicit_stroke, family),
	}
}

const num_or = (v: unknown, fallback: number, min: number, max: number): number =>
	typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback

/** Un simbol de categorie, cu moștenirea de mai sus. */
function resolve_symbol(
	raw: unknown,
	base: CategorySymbol,
	explicit_stroke: boolean,
	_family: StyleFamily,
): CategorySymbol {
	const r = as_style(raw)
	const color = typeof r.color === "string" && r.color !== "" ? r.color : base.color
	const stroke =
		typeof r.stroke === "string" && r.stroke !== ""
			? r.stroke
			: explicit_stroke
				? base.stroke
				: color
	return {
		color,
		opacity: num_or(r.opacity, base.opacity, 0, 1),
		stroke,
		width: num_or(r.width, base.width, 0, 50),
		hatch: parse_hatch(r.hatch, color),
	}
}

/** O condiție din JSON liber → forma CANONICĂ (eq cu o valoare, in cu 2+, literalele pliate), sau null. */
export function parse_condition(raw: unknown, default_attr = ""): CategoryCondition | null {
	const r = as_style(raw)
	// ⚠️ numele atributului NU se taie: cheile EAV sunt exact numele coloanelor proiectantului, cu tot cu
	// spațiile de la margini („Regim juridic ” la Crețeni, „ Tip_SZF”) — tăiat, nu s-ar mai potrivi cu datele
	const attr = typeof r.attr === "string" && r.attr.trim() !== "" ? r.attr : default_attr
	if (attr === "") {
		return null
	}
	const fold: CategoryCondition["fold"] = r.fold === "upper" || r.fold === "lower" ? r.fold : ""
	const fb = typeof r.fallback === "string" && r.fallback.trim() !== "" ? r.fallback : ""
	const fallback = fb !== attr ? fb : ""
	let op: CategoryOp =
		typeof r.op === "string" && (CATEGORY_OPS as readonly string[]).includes(r.op)
			? (r.op as CategoryOp)
			: "eq"
	const trim = r.trim === true
	if (op === "empty" || op === "not_empty") {
		return { attr, op, values: [], fold, trim, fallback }
	}
	const raw_values = Array.isArray(r.values) ? r.values : r.value === undefined ? [] : [r.value]
	const values = [
		...new Set(
			raw_values
				.map((v) => (typeof v === "string" ? v : typeof v === "number" ? String(v) : ""))
				.filter((v) => v !== "")
				.map((v) => (fold === "upper" ? v.toUpperCase() : fold === "lower" ? v.toLowerCase() : v)),
		),
	]
	if (values.length === 0) {
		return null
	}
	if (op === "eq" && values.length > 1) {
		op = "in"
	} else if (op === "in" && values.length === 1) {
		op = "eq"
	} else if (op === "neq" && values.length > 1) {
		op = "not_in"
	} else if (op === "not_in" && values.length === 1) {
		op = "neq"
	}
	return { attr, op, values, fold, trim, fallback } // prefix/suffix/contains cu mai multe valori = oricare dintre ele
}

/** Condiția SIMPLĂ: egalitate/prefix pe atributul principal, fără normalizare și fără rezervă — forma istorică. */
export const is_simple_condition = (c: CategoryCondition, attr: string): boolean =>
	c.attr === attr &&
	(c.op === "eq" || c.op === "prefix") &&
	c.fold === "" &&
	!c.trim &&
	c.fallback === ""

const OP_TEXT: Record<CategoryOp, string> = {
	eq: "=",
	neq: "≠",
	in: "∈",
	not_in: "∉",
	prefix: "începe cu",
	suffix: "se termină cu",
	contains: "conține",
	empty: "gol",
	not_empty: "nevid",
}

/** Textul unei condiții, pentru afișare (legendă/panou) și ca identitate a categoriei compuse. */
export function condition_text(c: CategoryCondition): string {
	const a = c.fallback ? `${c.attr}|${c.fallback}` : c.attr
	if (c.op === "empty" || c.op === "not_empty") {
		return `${a} ${OP_TEXT[c.op]}`
	}
	const vals = c.values.length === 1 ? c.values[0] : `{${c.values.join(", ")}}` // mai multe = oricare dintre ele
	return `${a} ${OP_TEXT[c.op]} ${vals}`
}
export const conditions_text = (cs: CategoryCondition[]): string =>
	cs.map(condition_text).join(" ∧ ")

/**
 * Citire TOLERANTĂ a lui `style.categories` (JSON liber din admin/import): listă de valori — forma simplă
 * `{value, match}` pe `attr` (nevid) sau forma compusă `{conditions: [...]}` —, fără duplicate pe condiții,
 * cel mult MAX_CATEGORIES; altfel null.
 */
export function parse_categories(
	raw: unknown,
	base: CategorySymbol,
	explicit_stroke: boolean,
	family: StyleFamily,
): EffectiveCategories | null {
	const c = as_style(raw)
	const attr = typeof c.attr === "string" && c.attr.trim() !== "" ? c.attr : "" // exact, netăiat (vezi parse_condition)
	if (!Array.isArray(c.values)) {
		return null
	}
	const values: EffectiveCategory[] = []
	const seen = new Set<string>()
	for (const item of c.values) {
		if (values.length >= MAX_CATEGORIES) {
			break
		}
		const it = as_style(item)
		let conditions: CategoryCondition[]
		if (Array.isArray(it.conditions)) {
			conditions = it.conditions
				.map((x) => parse_condition(x, attr))
				.filter((x): x is CategoryCondition => x !== null)
		} else {
			const value =
				typeof it.value === "string"
					? it.value
					: typeof it.value === "number"
						? String(it.value)
						: ""
			if (attr === "" || value === "") {
				continue
			}
			conditions = [
				{
					attr,
					op: it.match === "prefix" ? "prefix" : "eq",
					values: [value],
					fold: "",
					trim: false,
					fallback: "",
				},
			]
		}
		if (conditions.length === 0) {
			continue
		}
		const key = conditions.map((x) => JSON.stringify(x)).join("&")
		if (seen.has(key)) {
			continue
		}
		seen.add(key)
		const simple = conditions.length === 1 && is_simple_condition(conditions[0], attr)
		const value = simple ? conditions[0].values[0] : conditions_text(conditions)
		values.push({
			value,
			match: simple && conditions[0].op === "prefix" ? "prefix" : "eq",
			label: typeof it.label === "string" && it.label.trim() !== "" ? it.label : value,
			conditions,
			...resolve_symbol(it, base, explicit_stroke, family),
		})
	}
	if (values.length === 0) {
		return null
	}
	const attrs: string[] = []
	for (const a of [
		attr,
		...values.flatMap((v) => v.conditions.flatMap((x) => [x.attr, x.fallback])),
	]) {
		if (a !== "" && !attrs.includes(a)) {
			attrs.push(a)
		}
	}
	const has_else = typeof c.else === "object" && c.else !== null
	return {
		attr: attr || attrs[0],
		attrs,
		values,
		else: has_else ? resolve_symbol(c.else, base, explicit_stroke, family) : base,
		has_else,
	}
}

/** Categoriile cu atributele REDENUMITE (export GML/SHP: numele câmpului așa cum ajunge în fișier). */
export function rename_category_attrs(
	c: EffectiveCategories,
	rename: (attr: string) => string,
): EffectiveCategories {
	const attrs: string[] = []
	for (const a of c.attrs.map(rename)) {
		if (!attrs.includes(a)) {
			attrs.push(a)
		}
	}
	return {
		...c,
		attr: rename(c.attr),
		attrs,
		values: c.values.map((v) => ({
			...v,
			conditions: v.conditions.map((x) => ({
				...x,
				attr: rename(x.attr),
				fallback: x.fallback ? rename(x.fallback) : "",
			})),
		})),
	}
}

/** Atributele pe care le folosesc categoriile unui stil BRUT (`config.style`), principalul primul. */
export function category_attrs_raw(style: unknown): string[] {
	const c = as_style(as_style(style).categories)
	const out: string[] = []
	const add = (a: unknown): void => {
		if (typeof a === "string" && a.trim() !== "" && !out.includes(a)) {
			out.push(a)
		}
	}
	add(c.attr)
	if (Array.isArray(c.values)) {
		for (const it of c.values) {
			const conds = as_style(it).conditions
			if (Array.isArray(conds)) {
				for (const x of conds) {
					const xc = as_style(x)
					add(xc.attr)
					add(xc.fallback)
				}
			}
		}
	}
	return out
}

// ─────────────────────────────────────────────────────────────
// Expresiile de desenare (MapLibre) — PURE, ca să fie testabile fără browser
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// Expresiile de desenare (MapLibre) — PURE, ca să fie testabile fără browser
// ─────────────────────────────────────────────────────────────

/** Expresie MapLibre (listă JSON) sau valoare constantă. */
export type PaintValue = string | number | unknown[]

/** Operandul unei condiții: atributul ca TEXT (`to-string` peste `coalesce(…, "")`), cu rezerva și plierea lui. */
function operand_expr(c: CategoryCondition): unknown[] {
	const text = (a: string): unknown[] => ["to-string", ["coalesce", ["get", a], ""]]
	let o: unknown[] = c.fallback
		? ["case", ["!=", text(c.attr), ""], text(c.attr), text(c.fallback)]
		: text(c.attr)
	if (c.fold === "upper") {
		o = ["upcase", o]
	} else if (c.fold === "lower") {
		o = ["downcase", o]
	}
	return o
}

/** Expresia booleană MapLibre a unei condiții (`trim` se ignoră — n-are echivalent în expresii). */
export function condition_expr(c: CategoryCondition): unknown[] {
	const o = operand_expr(c)
	const v = c.values[0] ?? ""
	switch (c.op) {
		case "eq":
			return ["==", o, v]
		case "neq":
			return ["!=", o, v]
		case "in":
			return ["match", o, c.values, true, false]
		case "not_in":
			return ["!", ["match", o, c.values, true, false]]
		case "prefix":
			return any_of(c.values, (x) => ["==", ["slice", o, 0, x.length], x])
		case "suffix":
			return any_of(c.values, (x) => [
				"all",
				[">=", ["length", o], x.length],
				["==", ["slice", o, ["-", ["length", o], x.length]], x],
			])
		case "contains":
			return any_of(c.values, (x) => ["in", x, o])
		case "empty":
			return ["==", o, ""]
		case "not_empty":
			return ["!=", o, ""]
	}
}

/** `any` peste valori — o singură valoare rămâne expresia ei (fără `any` de prisos). */
const any_of = (values: string[], one: (v: string) => unknown[]): unknown[] =>
	values.length === 1 ? one(values[0]) : ["any", ...values.map(one)]

/** Condiția unei categorii: conjuncția (`all`) condițiilor ei. */
export function category_expr(v: EffectiveCategory): unknown[] {
	return v.conditions.length === 1
		? condition_expr(v.conditions[0])
		: ["all", ...v.conditions.map(condition_expr)]
}

/**
 * Valoarea unei proprietăți de vopsea pe categorii: `["case", cond₁, v₁, …, v_altfel]`. Atributul se
 * compară ca TEXT (`to-string` peste `coalesce(…, "")`) — un cod numeric sau lipsă nu aruncă în expresie,
 * iar QGIS compară la fel (`"attr" = 'v'`). Prefixul = `slice` pe lungimea prefixului; condițiile compuse
 * (15.10) prin `category_expr`.
 */
export function category_paint(
	cats: EffectiveCategories,
	pick: (sym: CategorySymbol) => string | number,
): PaintValue {
	const expr: unknown[] = ["case"]
	for (const v of cats.values) {
		expr.push(category_expr(v), pick(v))
	}
	expr.push(pick(cats.else))
	return expr
}

/** Proprietățile de vopsea ale unui strat — constante fără categorii, expresii `case` cu ele. */
export function paint_values(es: EffectiveStyle): {
	color: PaintValue
	opacity: PaintValue
	stroke: PaintValue
	width: PaintValue
} {
	const c = es.categories
	if (!c) {
		return { color: es.color, opacity: es.opacity, stroke: es.stroke, width: es.width }
	}
	return {
		color: category_paint(c, (s) => s.color),
		opacity: category_paint(c, (s) => s.opacity),
		stroke: category_paint(c, (s) => s.stroke),
		width: category_paint(c, (s) => s.width),
	}
}

/**
 * Categoriile pe calea PUBLICĂ (F10): dacă atributul lor NU e în allow-list-ul stratului, stilul pleacă
 * FĂRĂ categorii — portalul n-ar primi atributul în GeoJSON/tile, deci ar desena totul cu regula
 * „altfel", iar lista de valori din stil ar divulga vocabularul unui atribut restricționat.
 */
export function categories_allowed(style: unknown, allow: string[] | null): boolean {
	return allow === null || category_attrs_raw(style).every((a) => allow.includes(a))
}

/**
 * Culoare CSS → „#rrggbb" (minuscule) sau "" dacă nu se poate traduce (nume ca „red", gradienți…).
 * Harta acceptă orice culoare CSS, dar QGIS/SLD cer hex; ce nu se traduce cade pe paletă la export.
 */
export function to_hex(v: unknown): string {
	if (typeof v !== "string") {
		return ""
	}
	const s = v.trim().toLowerCase()
	const m6 = s.match(/^#([0-9a-f]{6})$/)
	if (m6) {
		return `#${m6[1]}`
	}
	const m3 = s.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/)
	if (m3) {
		return `#${m3[1]}${m3[1]}${m3[2]}${m3[2]}${m3[3]}${m3[3]}`
	}
	const rgb = s.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/)
	if (rgb) {
		return `#${hex2(rgb[1])}${hex2(rgb[2])}${hex2(rgb[3])}`
	}
	return ""
}
const hex2 = (n: string): string => Math.min(255, Number(n)).toString(16).padStart(2, "0")
